import { inject, Service } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { MbmError } from '@errors/mbm-error';
import { environment } from '@environments/environment';
import { IHttpAuthHandler } from './http-auth-handler';

@Service()
export class HttpService {
  private readonly http = inject(HttpClient);
  private readonly httpAuthHandler = inject(IHttpAuthHandler);
  private readonly baseUrl = environment.apiUrl;

  getObjectAsync<T>(path: string): Promise<T> {
    return this.manageRequestAsync(() => this.http.get<T>(`${this.baseUrl}${path}`));
  }

  postObjectAsync<T, R>(path: string, body: T): Promise<R> {
    return this.manageRequestAsync(() => this.http.post<R>(`${this.baseUrl}${path}`, body));
  }

  patchObjectAsync<T, R>(path: string, body: T): Promise<R> {
    return this.manageRequestAsync(() => this.http.patch<R>(`${this.baseUrl}${path}`, body));
  }

  putObjectAsync<T, R>(path: string, body: T): Promise<R> {
    return this.manageRequestAsync(() => this.http.put<R>(`${this.baseUrl}${path}`, body));
  }

  deleteAsync<R>(path: string): Promise<R> {
    return this.manageRequestAsync(() => this.http.delete<R>(`${this.baseUrl}${path}`));
  }

  getBlobAsync(path: string): Promise<HttpResponse<Blob>> {
    return this.manageRequestAsync(() => this.http.get(`${this.baseUrl}${path}`, { observe: 'response', responseType: 'blob' }));
  }

  private async manageRequestAsync<T>(requestFactory: () => Observable<T>): Promise<T> {
    try {
      return await firstValueFrom(requestFactory());
    } catch (error: unknown) {
      if ((error as any)?.status === 401) {
        return this.retryAfterSilentAuth(requestFactory);
      }
      throw error instanceof MbmError ? error : this.createMbmError(error);
    }
  }

  private async retryAfterSilentAuth<T>(requestFactory: () => Observable<T>): Promise<T> {
    try {
      await this.httpAuthHandler.silentAuthenticate();
      return await firstValueFrom(requestFactory());
    } catch (retryError: unknown) {
      if ((retryError as any)?.status === 401) {
        this.httpAuthHandler.handleAuthFailure();
      }
      throw retryError instanceof MbmError ? retryError : this.createMbmError(retryError);
    }
  }

  private createMbmError(error: any): MbmError {
    const mbmError = new MbmError();
    mbmError.statusCode = error?.status;
    mbmError.validationErrorCode = error?.error?.additionalData?.validationErrorCode ?? error?.error?.validationErrorCode;
    mbmError.validationMessage = error?.error?.additionalData?.validationMessage ?? error?.error?.validationMessage;
    mbmError.originalError = error;
    return mbmError;
  }
}
